import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminAppBody from "../../../../components/admin/AdminAppBody";
import useApiCallHooks from "../../../../hooks/useApiCallHooks";
import { toast } from "react-toastify";

export default function AttendanceList() {
  const navigate = useNavigate();

  // API hook
  const [response, loading, error, callAPI, statusCode] = useApiCallHooks();
  const [attendanceData, setAttendanceData] = useState([]);

  // Modal states
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);

  // Fetch attendance on mount
  useEffect(() => {
    callAPI("get", "student/get-attendance");
  }, []);

  // Handle attendance fetch response
  useEffect(() => {
    if (response?.data && statusCode === 200) {
      setAttendanceData(response.data);
    }
  }, [response, statusCode]);

  // Compute total hours
  const getTotalHours = (in_time, out_time) => {
    if (!in_time || !out_time) return "-";
    const [ih, im] = in_time.split(":").map(Number);
    const [oh, om] = out_time.split(":").map(Number);
    const inDate = new Date(0, 0, 0, ih, im);
    const outDate = new Date(0, 0, 0, oh, om);
    const diff = (outDate - inDate) / 60000;
    if (diff <= 0) return "-";
    return `${Math.floor(diff / 60)}h ${diff % 60}m`;
  };

  // Open modal
  const handleViewClick = (item) => {
    setSelectedItem({ ...item }); // clone to avoid direct mutation
    setShowModal(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedItem(null);
  };

  // Save changes
  const handleSave = async () => {
    if (!selectedItem?.id) return;
    setUpdateLoading(true);
    await callAPI("post", "student/update-attendance", {
      id: selectedItem.id,
      out_time: selectedItem.out_time,
      task_summary: selectedItem.task_summary,
    });

    // If update successful
    if (statusCode === 200 && 202) {
      toast.success("Attendance updated successfully!");
      // Update local state
      setAttendanceData((prev) =>
        prev.map((a) => (a.id === selectedItem.id ? selectedItem : a))
      );
      handleCloseModal();
      navigate("/attendance/list"); // ✅ Navigate back to list after save
    }
    setUpdateLoading(false);
  };

  return (
    <AdminAppBody
      loading={loading}
      content={
        <div className="container py-3">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0">Attendance List</h4>
            <button
              className="btn btn-primary"
              onClick={() => navigate("/attendance/add")}
            >
              Add
            </button>
          </div>

          {/* Table */}
          <div className="card shadow-sm">
            <div className="card-body p-0">
              {loading ? (
                <div className="p-3 text-center">Loading...</div>
              ) : attendanceData.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-striped table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Date</th>
                        <th>In Time</th>
                        <th>Out Time</th>
                        <th>Working Hours</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendanceData.map((item) => (
                        <tr key={item.id}>
                          <td>{item.date}</td>
                          <td>{item.in_time}</td>
                          <td>{item.out_time || "-"}</td>
                          <td>{getTotalHours(item.in_time, item.out_time)}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-info"
                              onClick={() => handleViewClick(item)}
                            >
                              View / Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-3 text-center">
                  No attendance records found.
                </div>
              )}
            </div>
          </div>

          {/* Modal */}
          {showModal && selectedItem && (
            <div
              className="modal fade show"
              style={{
                display: "block",
                backgroundColor: "rgba(0,0,0,0.5)",
              }}
            >
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Attendance Details</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={handleCloseModal}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <p>
                      <strong>Date:</strong> {selectedItem.date}
                    </p>

                    <div className="mb-3">
                      <label className="form-label">Out Time</label>
                      <input
                        type="time"
                        className="form-control"
                        value={selectedItem.out_time || ""}
                        onChange={(e) =>
                          setSelectedItem({
                            ...selectedItem,
                            out_time: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Task Summary</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={selectedItem.task_summary || ""}
                        onChange={(e) =>
                          setSelectedItem({
                            ...selectedItem,
                            task_summary: e.target.value,
                          })
                        }
                      ></textarea>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleCloseModal}
                    >
                      Close
                    </button>
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={handleSave}
                      disabled={updateLoading}
                    >
                      {updateLoading ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      }
    />
  );
}
